import { useQuery } from '@tanstack/react-query';

const MAX_SAMPLE_POINTS = 15;

// Evenly samples a route polyline down to a bounded number of points — the corridor traffic
// endpoint checks every point against the incident feed, so an OSRM route's full geometry
// (which can be hundreds of coordinates) would make that request needlessly large.
function samplePoints(coords: { lat: number; lng: number }[]): { lat: number; lng: number }[] {
  if (coords.length <= MAX_SAMPLE_POINTS) return coords;
  const step = (coords.length - 1) / (MAX_SAMPLE_POINTS - 1);
  return Array.from({ length: MAX_SAMPLE_POINTS }, (_, i) => coords[Math.round(i * step)]);
}

// Fetches the road-following pickup→delivery route (same public OSRM instance used by
// RelayMap.tsx/RouteMap/LiveTrackingMap for the map polyline) and returns a bounded set of
// sample points along it, for use as the "route corridor" in useRouteTrafficNews.
export function useRoutePoints(
  pickupLat: number, pickupLng: number, deliveryLat: number, deliveryLng: number, enabled = true
) {
  return useQuery<{ lat: number; lng: number }[]>({
    queryKey: ['route-points', pickupLat, pickupLng, deliveryLat, deliveryLng],
    queryFn: async () => {
      const res = await fetch(
        `https://router.project-osrm.org/route/v1/driving/${pickupLng},${pickupLat};${deliveryLng},${deliveryLat}?overview=full&geometries=geojson`
      );
      const data = await res.json();
      if (!data.routes?.length) return [{ lat: pickupLat, lng: pickupLng }, { lat: deliveryLat, lng: deliveryLng }];
      const coords: { lat: number; lng: number }[] = data.routes[0].geometry.coordinates.map(
        ([lng, lat]: [number, number]) => ({ lat, lng })
      );
      return samplePoints(coords);
    },
    enabled,
    staleTime: Infinity,
    retry: false,
  });
}
